/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useState } from "react";
import { Undo2, Loader2, Eye, ShieldAlert, History } from "lucide-react";
import { toast } from "../../../components/ui/sonner";
import { fmtD } from "./meta";
import { T } from "../../../lib/i18n";
// 【2026-10-29 时区统一】批次时间按洛杉矶显示：原来用浏览器默认 locale + 本机时区，
// 同一个批次中英文界面/中美两地看到的字符串都不一样（还会出现 上午/下午）。
import { fmtDT } from "../../../lib/usDate";

const fmtAt = (s) => (s ? fmtDT(s) : "—");

/* 撤销「经理集中设置」：列出最近 30 天的批量设置批次 → 预览受影响项目与原日期 → 确认撤销（恢复原日期 / 解除锁定） */
export const SignDateUndo = ({ api, onChanged }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [pv, setPv] = useState(null);       // {key, rows, count}
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/design/v3/sign-date/batches", { params: { days: 30 } }).then(({ data: d }) => setData(d)).catch(() => setData({ batches: [] }));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const batches = data?.batches || [];
  if (!batches.length) return null;
  const preview = async (b) => {
    setBusy(true); setConfirming(false);
    try {
      const { data: r } = await api.post("/design/v3/sign-date/undo-batch", { activityIds: b.items.map((i) => i.activityId), dryRun: true });
      setPv({ key: b.key, batch: b, ...r });
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const run = async () => {
    setBusy(true);
    try {
      const ids = pv.rows.filter((r) => r.ok).map((r) => r.activityId);
      const { data: r } = await api.post("/design/v3/sign-date/undo-batch", { activityIds: ids, dryRun: false });
      toast.success(T("已撤销 {0} 个项目的批量签约日期，已恢复原日期 / 自动识别", [r.undone]));
      setPv(null); setConfirming(false); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const revertable = batches.reduce((s, b) => s + b.revertable, 0);
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs space-y-2" data-testid="dv3-sd-undo-panel" data-batches={batches.length} data-revertable={revertable}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-bold text-amber-900 inline-flex items-center gap-1.5"><History className="h-4 w-4" />{T("最近 30 天的批量设置记录 {0} 批", [batches.length])}{revertable > 0 && <span className="text-amber-700 font-normal">{T("（可撤销 {0} 个项目）", [revertable])}</span>}</p>
        <button type="button" onClick={() => setOpen(!open)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto text-amber-800 font-semibold hover:underline" data-testid="dv3-sd-undo-toggle">{open ? T("收起") : T("展开 / 撤销误操作")}</button>
      </div>
      {open && (
        <div className="space-y-1.5" data-testid="dv3-sd-undo-list">
          {batches.map((b) => (
            <div key={b.key} className="rounded-lg bg-white border border-amber-100 px-3 py-2 flex flex-wrap items-center gap-2" data-testid={`dv3-sd-batch-${b.key}`} data-count={b.count} data-revertable={b.revertable}>
              <span className="font-semibold text-slate-800">{b.actor}</span>
              <span className="text-slate-500">{fmtAt(b.firstAt)}{b.lastAt !== b.firstAt ? ` ~ ${fmtAt(b.lastAt)}` : ""}</span>
              <span className="text-slate-700">{T("统一设为")} <b className="text-violet-700">{fmtD(b.date)}</b> · {T("{0} 个项目", [b.count])}</span>
              {b.undone > 0 && <span className="text-emerald-700">{T("已撤销 {0}", [b.undone])}</span>}
              {b.revertable < b.count - b.undone && <span className="text-slate-400">{T("另 {0} 个已被其他操作改动", [b.count - b.undone - b.revertable])}</span>}
              <button type="button" disabled={busy || !b.revertable} onClick={() => preview(b)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto inline-flex items-center gap-1 rounded-full border border-amber-400 text-amber-900 px-3 py-1 font-semibold hover:bg-amber-100 disabled:opacity-40" data-testid={`dv3-sd-batch-preview-${b.key}`}>{busy && pv?.key !== b.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}{T("预览撤销")}</button>
            </div>
          ))}
          {pv && (
            <div className="rounded-lg bg-white border border-amber-300 p-3 space-y-2" data-testid="dv3-sd-undo-preview" data-count={pv.count}>
              <p className="font-bold text-slate-800">{T("将撤销 {0} 个项目（{1} 统一设为 {2}），恢复为原日期 / 自动识别：", [pv.count, pv.batch.actor, fmtD(pv.batch.date)])}</p>
              <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
                {pv.rows.map((r) => (
                  <div key={r.activityId} className="flex-wrap md:flex-nowrap flex items-center gap-2 py-1" data-testid={`dv3-sd-undo-row-${r.projectId}`} data-ok={r.ok ? 1 : 0}>
                    <span className="font-semibold text-slate-700 truncate flex-1">{r.address || r.projectId}<span className="text-slate-400 font-normal"> · {r.projectNo}</span></span>
                    <span className="text-slate-500">{fmtD(r.new)} → <b className={r.ok ? "text-emerald-700" : "text-slate-400"}>{r.old && r.old !== "—" ? fmtD(r.old) : T("自动识别")}</b></span>
                    {!r.ok && <span className="text-[10px] text-red-600">{r.error}</span>}
                  </div>
                ))}
              </div>
              {confirming ? (
                <div className="rounded-lg border-2 border-red-300 bg-red-50 p-2.5 space-y-2" data-testid="dv3-sd-undo-confirm-box">
                  <p className="font-bold text-red-800 inline-flex items-center gap-1.5"><ShieldAlert className="h-4 w-4" />{T("确定撤销这 {0} 个项目的签约日期？恢复后「经理设置」锁定解除，日期回到原值或 CRM 自动识别。", [pv.count])}</p>
                  <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
                    <button type="button" onClick={() => setConfirming(false)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 rounded-full border border-slate-300 px-3 py-1" data-testid="dv3-sd-undo-cancel">{T("返回")}</button>
                    <button type="button" disabled={busy} onClick={run} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 rounded-full bg-red-600 text-white font-bold px-4 py-1 disabled:opacity-50" data-testid="dv3-sd-undo-ok">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}{T("确定撤销")}</button>
                  </div>
                </div>
              ) : (
                <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
                  <button type="button" onClick={() => setPv(null)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 rounded-full border border-slate-300 px-3 py-1" data-testid="dv3-sd-undo-close">{T("关闭")}</button>
                  <button type="button" disabled={!pv.count} onClick={() => setConfirming(true)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 rounded-full bg-amber-600 text-white font-bold px-4 py-1 disabled:opacity-40" data-testid="dv3-sd-undo-submit"><Undo2 className="h-3.5 w-3.5" />{T("撤销 {0} 个", [pv.count])}</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
